import React, { useEffect, useMemo, useState } from "react";
import "./Leaderboard.css";

const Leaderboard = ({
  limit = 10,
  scope = "any", // "any" | "pro" | "block"
  compact = false, // compact mode for embedding on other pages
  title = "🏆 Leaderboard",
}) => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const run = async () => {
      setLoading(true);
      setErr(null);
      try {
        const res = await fetch(`${baseUrl}/users/leaderboard?limit=${limit}&scope=${scope}`);
        if (!res.ok) throw new Error("Failed to fetch leaderboard");
        const data = await res.json();
        if (isMounted) setRows(data.leaderboard || []);
      } catch (e) {
        if (isMounted) setErr(e.message || "Error");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    run();
    return () => { isMounted = false; };
  }, [baseUrl, limit, scope]);

  const maxSolved = useMemo(
    () => rows.reduce((m, r) => Math.max(m, r.solved || 0), 0) || 1,
    [rows]
  );

  return (
    <div className={`lb-wrapper ${compact ? "lb-compact" : ""}`}>
      <div className="lb-header">
        <h2 className="lb-title">{title}</h2>
        <span className="lb-scope-badge" title="Counting unique problems solved">
          {scope === "pro" ? "Pro Mode" : scope === "block" ? "Block Mode" : "All Modes"}
        </span>
      </div>

      {loading && (
        <div className="lb-skeleton">
          {Array.from({ length: Math.min(5, limit) }).map((_, i) => (
            <div key={i} className="lb-skel-row" />
          ))}
        </div>
      )}

      {err && !loading && <div className="lb-error">⚠️ {err}</div>}

      {!loading && !err && rows.length === 0 && (
        <div className="lb-empty">No solves yet—be the first to climb the board!</div>
      )}

      {!loading && !err && rows.length > 0 && (
        <ol className="lb-list">
          {rows.map((r) => {
            const pct = Math.round(((r.solved || 0) / maxSolved) * 100);
            const badge =
              r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : r.rank === 3 ? "🥉" : null;

            return (
              <li key={r.userId} className={`lb-item ${r.rank <= 3 ? `top-${r.rank}` : ""}`}>
                <div className="lb-left">
                  <div className="lb-rank">{r.rank}</div>
                  <div className="lb-username">
                    {badge && <span className="lb-medal">{badge}</span>}
                    <span className="lb-name" title={r.username}>{r.username}</span>
                  </div>
                </div>

                <div className="lb-right">
                  <div className="lb-count">
                    {r.solved} {r.solved === 1 ? "solve" : "solves"}
                  </div>
                  <div className="lb-bar">
                    <div className="lb-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default Leaderboard;
